import React, { useState, useCallback, useEffect, useRef } from 'react'
import { BUSINESS_ACCOUNTS_URL } from 'config/config';
import { BUSINESS_ACCOUNT_TYPES_URL } from 'config/config';
import { FormContainer } from 'uiwidgets/FormContainer'
import { FilesField } from 'uiwidgets/FilesField';
import { FormField } from 'uiwidgets/FormField';
import { Row, Col, Alert } from 'react-bootstrap';
import { SubmitButton } from 'uiwidgets/SubmitButton';
import { API } from 'api/API';
import { extractError } from 'api/ErrorUtil';
import { isValidUUID } from 'util/Validators';
import AppSpinner from 'uiwidgets/AppSpinner';
import _ from "lodash";

const ImportAccountsPage = () => {

    const initialFormState = {
        errors: {},
        infoMessage: "",
        readOnly: false,
        importFile: "",
        accountTypeId: "",
        accountTypes: [],
        status: ""
    };

    const initialValidationState = {
        importFileError: false,
        importFileErrMsg: "",
        accountTypeIdError: false,
        accountTypeIdErrMsg: ""
    };

    let [formData, setFormData] = useState(initialFormState);
    let [formValidityData, setFormValidityData] = useState(initialValidationState);
    const importFileInput = useRef(null);

    const messageReset = () => {
        return {
            infoMessage: "",
            errors: {}
        }
    }

    const onToastClose = () => {
        setFormData({
            ...formData,
            ...messageReset()
        });
    }

    const handleValidation = async (event) => {
        const name = event.target.name;
        const value = event.target.value;
        let { hasError, error } = validateInput(name, value);
        setFormValidityData({
            ...formValidityData,
            [name + 'Error']: hasError,
            [name + 'ErrMsg']: error
        })
    }

    const handleDropDownChange = (event) => {
        const name = event.target.name;
        const value = event.target.value;
        setFormData({
            ...formData,
            [name]: value
        });
        handleValidation(event);
    }

    const handleImportFileChange = async (event) => {
        if (formData.readOnly) {
            return;
        }
        const value = event.target.files[0];
        setFormData({
            ...formData,
            importFile: value,
            ...messageReset()
        })
    };

    const handleImportFileValidation = async (event) => {
        if (!formData.importFile) {
            setFormValidityData({
                ...formValidityData,
                importFileError: true,
                importFileErrMsg: "Import file is required"
            })
            return false;
        }
        if (formData.importFile.size > 50 * 1024 * 1024) {
            setFormValidityData({
                ...formValidityData,
                importFileError: true,
                importFileErrMsg: "Import file size cannot exceed 200MB"
            })
            return false;
        }

        setFormValidityData({
            ...formValidityData,
            importFileError: false,
            importFileErrMsg: ""
        })
    }

    const fetchAccountTypes = useCallback(async () => {
        let accountTypes = null;
        try {
            let response = await API.get(BUSINESS_ACCOUNT_TYPES_URL)
            accountTypes = response.data;
            let initAccountTypeId = "";
            if (accountTypes.length === 1) {
                initAccountTypeId = accountTypes[0].id;
            }
            setFormData({
                ...formData,
                accountTypes: accountTypes,
                accountTypeId: initAccountTypeId
            })
        } catch (error) {
            let { message, errors, httpStatus } = extractError(error);
            if (httpStatus === 404) {
                setFormData({ ...formData, errors: { ...errors, message: "Account Types not found" } });
            } else {
                setFormData({ ...formData, errors: errors });
            }
            return;
        }
    });

    useEffect(() => {
        fetchAccountTypes();
    }, []);


    const validateInput = (name, value) => {
        let hasError = false,
            error = ""
        switch (name) {
            case "accountTypeId":
                if (value.trim() === "") {
                    hasError = true
                    error = "Account Type is required"
                } else if (!isValidUUID(value)) {
                    hasError = true
                    error = "Invalid account type";
                } else {
                    hasError = false
                    error = ""
                }
                break
            default:
                break;
        }
        return { hasError, error };
    }

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!formData.importFile) {
            let hasError = true;
            let error = "Select a file to upload"

            setFormValidityData({
                ...formValidityData,
                importFileError: hasError,
                importFileErrMsg: error
            })
            return false;
        }

        let field = document.getElementById('accountTypeId');
        let fieldName = field.name;
        let fieldValue = field.value;
        let valResult = validateInput(fieldName, fieldValue);
        setFormValidityData({
            ...formValidityData,
            [fieldName + 'Error']: valResult.hasError,
            [fieldName + 'ErrMsg']: valResult.error
        })
        if (valResult.hasError) {
            alert("Form has validation error")
            return false;
        }

        const importData = new FormData();
        importData.append('file', formData.importFile);
        importData.append('accountTypeId', fieldValue);

        try {

            setFormData({
                ...formData,
                status: "importing"
            })
            // Send formData to your API endpoint using Axios
            await API.post(BUSINESS_ACCOUNTS_URL + '/import', importData)

            setFormData({
                ...initialFormState,
                accountTypes: formData.accountTypes,
                accountTypeId: formData.accountTypeId,
                infoMessage: "Accounts Data was imported successfully",
            });
            if (importFileInput.current) {
                importFileInput.current.value = "";
                importFileInput.current.type = "file";
            }
            setFormValidityData(initialValidationState);

        } catch (error) {
            let errors = extractError(error);
            setFormData({
                ...formData,
                status:"",
                errors: errors
            });
        }
    };


    return (
        <React.Fragment>
            <FormContainer title="Import Accounts" submitButtonFunction={handleSubmit}
                errors="" infoMessage={formData.infoMessage} toastCloseFunc={onToastClose}>
                {formData.status === 'importing' && <Row><Col>
                    <AppSpinner />&nbsp;Please wait while payment methods are setup!
                </Col></Row>}
                <Row>
                    <Col mb={6} lg={6} sm={6}>
                        <FormField label="Account Type" type="select" name="accountTypeId"
                            value={formData.accountTypeId || "blank"} onChange={handleDropDownChange} valstate={!formValidityData.accountTypeIdError}
                            valMsg={formValidityData.accountTypeIdErrMsg} readOnly={formData.readOnly || formData.accountTypes.length === 1}>
                            <option value="blank" key="blank">Select Account Type</option>
                            {formData.accountTypes.map((type, idx) => {
                                return (
                                    <option value={type.id} key={idx}>
                                        {type.accountTypeDisplayName}
                                    </option>
                                )
                            })}
                        </FormField>
                    </Col>
                </Row>
                <Row>
                    <Col><hr />
                        Prove a csv file to import. The file contents must follow the format given below:
                        <ul>
                            <li>First/Header row is ignore</li>
                            <li>Columns must following the order
                                Company Name, First Name, Last Name, address, city, state, zip, country, email, mobile phone number
                            </li>
                        </ul>
                        <br />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <FilesField name="importFile" placeholder="Import sponsorship Data" accept=".csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            onChange={handleImportFileChange} valstate={!formValidityData.importFileError} valMsg={formValidityData.importFileErrMsg}
                            rref={importFileInput} onBlur={handleImportFileValidation} />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <SubmitButton label="Import Accounts" disabled={formData.readOnly} className="mt-4" />
                    </Col>
                </Row>


            </FormContainer>
            {!_.isEmpty(formData.errors?.errors) && <span>
                {Object.entries(formData.errors.errors).map(([errorCode, errorMessage]) => {
                return (
                    <div key={errorCode}>
                    <strong>{errorCode}:</strong> {errorMessage}
                  </div>
                );
                })}
            </span>}
        </React.Fragment>
    )
}

export default ImportAccountsPage